import React, { useState } from 'react'
import { ChevronDownIcon, DocumentMagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { useMutation } from '@tanstack/react-query'
import { handlePostRequest } from '../../utils/api-handler.util'
import { QueryKeys } from '../../utils/queryKeyGenerator.js'
import { showToaster } from '../../utils/show-toaster.util'
import RequestSubmittedMessage from './RequestSubmittedMessage'
import { useFileUpload } from '../../hooks/useFileUpload.jsx'
import useProductCategories from '../../hooks/useProductCategories.js'

const ProductFinderForm = () => {
  const { categories } = useProductCategories()
  const { renderUploadInput, handleFileUpload, isUploading } = useFileUpload()
  const [selectedCategory, setSelectedCategory] = useState('')
  const [quantity, setQuantity] = useState(1)
  const [description, setDescription] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [newRequestId, setNewRequestId] = useState(null)

  const productFinderMutation = useMutation({
    mutationFn: (formData) => handlePostRequest('/customers/product-finders', formData),
    onSuccess: (data) => {
      showToaster('success', 'Product finder request submitted successfully')
      const createdRequestId = data?.data?.bidRequest?.id
      setIsSubmitted(true)

      if (createdRequestId) {
        setNewRequestId(createdRequestId)
      }

      setSelectedCategory('')
      setDescription('')
      QueryKeys.bidsList.invalidateAll()
    },
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!selectedCategory) {
      showToaster('error', 'Please select a product category')
    }
    if (!description) {
      showToaster('error', 'Please provide a description')
      return
    }

    const uploadedFileUrl = await handleFileUpload()

    if (!uploadedFileUrl) {
      showToaster('error', 'Please upload an image')
      return
    }

    const formData = new FormData()
    formData.append('categoryId', selectedCategory)
    formData.append('description', description)
    formData.append('imageUrl', uploadedFileUrl)
    formData.append('requestQuantity', quantity)
    productFinderMutation.mutateAsync(formData)
  }

  const getSubmitButtonText = () => {
    if (isUploading) {
      return 'Uploading product image...'
    }

    if (productFinderMutation.isLoading) {
      return 'Submitting...'
    }
    return 'Submit'
  }

  return isSubmitted ? (
    <RequestSubmittedMessage requestId={newRequestId} />
  ) : (
    <div className="bg-background lg:bg-neutral-200-50 lg:rounded-lg lg:p-8 lg:shadow-sm">
      <div className="text-center mb-6 lg:mb-8">
        <DocumentMagnifyingGlassIcon className="w-16 h-16 lg:w-20 lg:h-20 mx-auto text-tertiary-600 mb-4" />
        <h1 className="text-xl lg:text-2xl font-bold text-black capitalize mb-2">
          I Couldn't Find My Product
        </h1>
        <p className="text-sm lg:text-base text-gray-600 leading-tight lg:leading-relaxed max-w-lg mx-auto">
          Did you know you can search for a product using our product finder tool? There are
          hundreds of stores eager to help.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="mt-8 lg:mt-10">
        <fieldset disabled={productFinderMutation.isLoading}>
          <div className="space-y-6 lg:space-y-8">
            <div>
              <label
                htmlFor="category"
                className="block text-sm lg:text-base font-medium text-gray-600 mb-2"
              >
                Select Product Category <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  id="category"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  required
                  className="w-full px-4 py-3 lg:py-4 border border-gray-300 rounded-md bg-secondary-50 appearance-none focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200"
                >
                  <option value="" disabled>
                    select a category
                  </option>
                  {categories?.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500 pointer-events-none" />
              </div>
            </div>

            <div>
              <label
                htmlFor="quantity"
                className="block text-sm lg:text-base font-medium text-gray-600 mb-2"
              >
                The Product Quantity <span className="text-red-500">*</span>
              </label>
              <input
                id="quantity"
                value={quantity}
                type="number"
                required
                onChange={(e) => setQuantity(e.target.value)}
                className="w-full px-4 py-3 lg:py-4 border border-gray-300 rounded-md bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base resize-none disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200"
              />
            </div>

            <div>
              <label
                htmlFor="description"
                className="block text-sm lg:text-base font-medium text-gray-600 mb-2"
              >
                Tell Us More About The Product <span className="text-red-600">*</span>
              </label>
              <textarea
                id="description"
                rows="4"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                placeholder="Describe the product you are looking for..."
                className="w-full px-4 py-3 lg:py-4 border border-gray-300 rounded-md bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base resize-none disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200"
              />
            </div>

            <div>{renderUploadInput()}</div>
          </div>

          <div className="pt-6 lg:pt-8 mt-8">
            <button
              type="submit"
              className={`w-full lg:max-w-sm lg:mx-auto flex justify-center py-3 lg:py-4 px-4 border border-transparent rounded-lg shadow-sm text-base lg:text-lg font-medium text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all duration-200 ${
                productFinderMutation.isLoading || isUploading ? 'opacity-70 cursor-wait' : ''
              }`}
              disabled={productFinderMutation.isLoading || isUploading}
            >
              {getSubmitButtonText()}
            </button>
          </div>
        </fieldset>
      </form>
    </div>
  )
}

export default ProductFinderForm
